import {
  AnimatedSprite,
  Application,
  Container,
  Graphics,
  Particle,
  ParticleContainer,
  Sprite,
  Text,
  TextStyle,
  type Texture,
  type Ticker,
} from 'pixi.js';

import { buildAssets, type GameAssets } from './textures';
import { formatMultiplier } from '../util/format';
import { play as playSound } from '../audio/player';

/**
 * The animated scene.
 *
 * It renders state; it never decides it. `setMultiplier` and `crash` are called
 * with values that came from the server, and the scene's only job is to make
 * them feel like something.
 */

/** How the intensity ramp is scaled: 1.00x is calm, this multiplier is flat out. */
const INTENSITY_CEILING_X100 = 2000;

const PUNCH_SPEED_IDLE = 0.22;
const PUNCH_SPEED_MAX = 1.25;

const SHAKE_MAX_PX = 9;
const CRASH_SHAKE_PX = 38;

/** Where the bag hangs in world space. The pendulum swings about this. */
const BAG_X = 560;

/** Where the boxer stands, and the ground both of them are on. */
const BOXER_X = 250;
const GROUND_Y = 300;

/**
 * The boxer's body in world space, for deciding when a grain of sand has landed
 * on him.
 *
 * A box rather than the silhouette: the figure is a baked texture, so there is
 * no geometry left to test against, and at the size a grain is drawn nobody can
 * tell that the shoulder is square. It is deliberately narrower than the sprite,
 * whose frame is mostly empty air around the figure.
 */
const BOXER_HIT = { left: BOXER_X - 52, right: BOXER_X + 54, top: 92, bottom: GROUND_Y - 8 };

/** The bust: chunks of the bag, and a great deal of sand. */
const CHUNK_COUNT = 90;
const SAND_COUNT = 230;

/**
 * How much of the sand is thrown at the boxer rather than merely outwards.
 *
 * The bag bursting *onto him* is the beat the scene is built around -- he is the
 * one who hit it -- and that does not happen by accident from a symmetric radial
 * burst, so this share of the plume is aimed.
 */
const SAND_AT_BOXER = 0.42;

const CHUNK_COLOURS = [0x8a6440, 0x6b4a2f, 0x9a7148, 0xd92b4b, 0xe8e2d6];
const SAND_COLOURS = [0xe3cba5, 0xd8bb90, 0xefdcc0, 0xc9a878];

/** The colour the boxer is dusted with while the sand is on him. */
const SAND_TINT = 0xe0cba8;

/**
 * Sand falls more slowly than a chunk of leather does. It is not the real ratio
 * -- a grain that size would hang in the air far longer -- but a plume that
 * lingers is what makes the burst read as sand rather than as gravel.
 */
const SAND_GRAVITY = 620;
const CHUNK_GRAVITY = 980;

interface Fragment {
  particle: Particle;
  container: ParticleContainer;
  vx: number;
  vy: number;
  spin: number;
  life: number;
  maxLife: number;
  /** Sand settles on things; a chunk of canvas tumbles past them. */
  sticky: boolean;
  /**
   * Set for the grains thrown at the boxer, whose path was solved as a ballistic
   * arc to a point on him.
   *
   * They carry no air drag, because that solution assumes there is none: with
   * drag applied the aimed plume stopped dead about a third of the way across
   * and the sand never reached the figure at all, which is the one thing the
   * effect exists to do.
   */
  guided: boolean;
  /** Set once the fragment has come to rest, on the boxer or on the floor. */
  landed: boolean;
}

export type ScenePhase = 'betting' | 'running' | 'crashed';

export class GameStage {
  private app!: Application;
  private assets!: GameAssets;

  /** Everything that shakes lives under here. */
  private world = new Container();
  private boxer!: AnimatedSprite;
  private bag!: Sprite;
  /** Chunks of the bag and grains of sand: one container per texture. */
  private chunks!: ParticleContainer;
  private sand!: ParticleContainer;
  private flash!: Graphics;
  private multiplierText!: Text;
  private statusText!: Text;

  private fragments: Fragment[] = [];

  private phase: ScenePhase = 'betting';
  private intensity = 0;

  private shakeUntil = 0;
  private shakeAmplitude = 0;
  private bagSwing = 0;
  private bagSwingVelocity = 0;
  private lastImpactFrame = -1;
  private elapsed = 0;

  /**
   * How covered in sand the boxer currently is, 0 to 1, and how hard he was
   * knocked back by it.
   *
   * The dusting is what makes the sand look like it hit *him* rather than like it
   * passed over his layer: without a reaction on the figure, a grain landing on
   * a shoulder is just a dot that stopped moving.
   */
  private boxerDust = 0;
  private boxerFlinch = 0;

  /** Set while the bag is in pieces, so it is not drawn until the next round. */
  private destroyed = false;

  private hostObserver: ResizeObserver | null = null;

  async mount(host: HTMLElement): Promise<void> {
    this.app = new Application();
    await this.app.init({
      background: 0x0a0e15,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      resizeTo: host,
      // The game is idle between rounds; letting Pixi decide when to render
      // keeps a phone from cooking during the betting window.
      preference: 'webgl',
    });
    host.appendChild(this.app.canvas);

    this.assets = await buildAssets(this.app.renderer);
    this.build();

    this.app.ticker.add(this.tick, this);
    this.layout();

    // The renderer's own resize event, not window.resize. Pixi applies a
    // resizeTo change on its own schedule, so a window listener runs while
    // app.screen still reports the old size -- which laid the scene out for the
    // previous viewport and pushed the bag and half the multiplier off a phone
    // screen. This fires after the resize has been applied.
    this.app.renderer.on('resize', this.layout, this);

    // And a resize of the host that is not a resize of the window.
    //
    // `resizeTo` only listens to window.resize, so when this host shrank because
    // the bet panels below it had finished laying out, Pixi never re-measured:
    // the canvas kept the height the host had a moment earlier and hung 27px
    // down over the first panel's label. Watching the host is what closes that
    // gap -- and it is only safe to do because the canvas is positioned
    // absolutely in CSS, so it cannot itself grow the box being observed.
    this.hostObserver = new ResizeObserver(() => this.app.resize());
    this.hostObserver.observe(host);
  }

  /**
   * Registers a callback on the renderer's ticker, ahead of the render itself.
   *
   * The multiplier is drawn twice -- as WebGL text on the canvas and as HTML on
   * the cash-out button -- and both have to show the same number. Updating them
   * from a separate requestAnimationFrame loop let Pixi render before the update
   * landed, so the canvas trailed the button by a frame and the two disagreed by
   * 0.01x. A ticker callback runs before the render in the same frame.
   */
  onFrame(callback: () => void): void {
    this.app.ticker.add(callback);
  }

  private build(): void {
    const backdrop = new Graphics();
    backdrop.rect(0, 0, 1, 1).fill(0x0d1219);
    backdrop.label = 'backdrop';
    this.app.stage.addChild(backdrop);

    const floor = new Sprite(this.assets.floor);
    floor.anchor.set(0, 1);
    this.world.addChild(floor);

    this.bag = new Sprite(this.assets.bag);
    // The pivot sits at the top of the bag, where the chain is, so rotation
    // swings it rather than spinning it about its middle.
    this.bag.anchor.set(0.5, 0.02);
    this.world.addChild(this.bag);

    this.boxer = new AnimatedSprite(this.assets.boxerFrames);
    this.boxer.anchor.set(0.5, 1);
    this.boxer.animationSpeed = PUNCH_SPEED_IDLE;
    this.boxer.play();
    this.world.addChild(this.boxer);

    // Two containers, because a ParticleContainer draws one texture: torn canvas
    // and grains of sand do not look alike and should not be the same quad. Sand
    // is added after the boxer so grains that settle on him are drawn on top of
    // him; the chunks go behind, since they are the far side of the bag coming
    // apart.
    this.chunks = this.buildParticles(this.assets.particle);
    this.world.addChildAt(this.chunks, this.world.children.indexOf(this.bag));
    this.sand = this.buildParticles(this.assets.sand);
    this.world.addChild(this.sand);

    this.app.stage.addChild(this.world);

    this.multiplierText = new Text({
      text: '1.00x',
      style: multiplierStyle(0x9aa4b2),
    });
    this.multiplierText.anchor.set(0.5);
    this.app.stage.addChild(this.multiplierText);

    this.statusText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'system-ui, sans-serif',
        fontSize: 17,
        fontWeight: '600',
        fill: 0x6b7686,
        letterSpacing: 1.4,
      }),
    });
    this.statusText.anchor.set(0.5);
    this.app.stage.addChild(this.statusText);

    this.flash = new Graphics();
    this.flash.rect(0, 0, 1, 1).fill(0xffffff);
    this.flash.alpha = 0;
    this.app.stage.addChild(this.flash);
  }

  private buildParticles(texture: Texture): ParticleContainer {
    return new ParticleContainer({
      texture,
      // Position, rotation, colour and alpha all animate per fragment, so they
      // have to be declared dynamic or the GPU buffer is never re-uploaded.
      dynamicProperties: { position: true, rotation: true, color: true },
    });
  }

  private layout(): void {
    const w = this.app.screen.width;
    const h = this.app.screen.height;

    const backdrop = this.app.stage.getChildByLabel('backdrop') as Graphics | null;
    if (backdrop) backdrop.setSize(w, h);
    this.flash.setSize(w, h);

    // The art is drawn in a fixed world space: the boxer stands at x=250 and the
    // bag hangs at x=560, both on a ground line at y=300. The pair occupies
    // roughly x 140..640, so that span is what has to fit and be centred --
    // scaling to the floor's full 900 would leave the action off to one side on
    // a phone.
    const contentWidth = 560 - 140 + 90; // boxer's left edge to the bag's right
    const contentHeight = 340;

    const scale = clamp(Math.min(w / (contentWidth * 1.15), h / contentHeight), 0.28, 1.4);
    this.world.scale.set(scale);

    // Centre the boxer-and-bag pair, and stand it on a ground line a little
    // above the bottom edge.
    const contentCentre = (140 + 650) / 2;
    const groundY = h * (w < 560 ? 0.82 : 0.88);
    this.world.position.set(w / 2 - contentCentre * scale, groundY - 300 * scale);

    // The floor has to reach both edges whatever the scale, so it is placed by
    // converting the screen's own edges into world units.
    //
    // The previous form used Math.abs(world.x), which is only the left edge's
    // distance when the world has been shifted left; centring the content on a
    // wide screen shifts it *right*, and the floor then stopped short of the
    // right-hand edge with a visible seam across the boards.
    const floor = this.world.children[0] as Sprite;
    const worldLeft = -this.world.x / scale;
    const worldRight = (w - this.world.x) / scale;
    floor.width = worldRight - worldLeft + 200;
    floor.position.set(worldLeft - 100, GROUND_Y);

    this.boxer.position.set(BOXER_X, GROUND_Y);
    this.bag.position.set(BAG_X, 8);

    // The multiplier sits above the action and is sized off the smaller axis, so
    // it cannot overflow a narrow screen.
    this.multiplierText.style.fontSize = clamp(Math.min(w, h) * 0.15, 34, 132);
    this.multiplierText.position.set(w / 2, h * (w < 560 ? 0.22 : 0.3));

    this.statusText.style.fontSize = w < 560 ? 13 : 17;
    this.statusText.position.set(
      w / 2,
      this.multiplierText.y + this.multiplierText.height * 0.72,
    );
  }

  // --------------------------------------------------------------------------
  // State the scene is driven by
  // --------------------------------------------------------------------------

  setPhase(phase: ScenePhase, statusLabel = ''): void {
    if (phase !== this.phase) {
      this.phase = phase;
      if (phase === 'betting') this.restoreBag();
    }
    this.statusText.text = statusLabel;
  }

  /** Called on every server tick and on every interpolated frame in between. */
  setMultiplier(x100: number): void {
    // Logarithmic, because the multiplier is exponential in time: a linear ramp
    // would sit at nothing for the first several seconds and then saturate.
    const ratio = Math.log2(Math.max(x100, 100) / 100) / Math.log2(INTENSITY_CEILING_X100 / 100);
    this.intensity = Math.max(0, Math.min(1, ratio));

    if (this.phase === 'running') {
      this.multiplierText.text = formatMultiplier(x100);
      this.multiplierText.style.fill = multiplierColour(x100);
    }
  }

  /** The bust: the bag comes apart, the screen flashes, everything shakes. */
  crash(crashX100: number): void {
    this.phase = 'crashed';
    this.multiplierText.text = formatMultiplier(crashX100);
    this.multiplierText.style.fill = 0xff4d4f;

    this.burst();
    this.destroyed = true;
    this.bag.visible = false;

    this.flash.alpha = 0.9;
    // A long decay rather than a single jolt: the shake is the loudest thing in
    // the scene at the moment a player finds out they lost a stake, and a rumble
    // that fades reads as an impact where an instant snap reads as a glitch.
    this.shake(CRASH_SHAKE_PX, 750);
  }

  /** Between rounds: reset the display without touching the animation. */
  resetForNextRound(): void {
    this.multiplierText.text = '1.00x';
    this.multiplierText.style.fill = 0x9aa4b2;
    this.intensity = 0;
    this.restoreBag();
  }

  destroy(): void {
    this.hostObserver?.disconnect();
    this.hostObserver = null;
    this.app?.destroy(true, { children: true });
  }

  private restoreBag(): void {
    this.destroyed = false;
    this.bag.visible = true;
    this.bag.rotation = 0;
    this.bagSwing = 0;
    this.bagSwingVelocity = 0;
    this.boxerDust = 0;
    this.boxerFlinch = 0;
    this.boxer.tint = 0xffffff;
    this.clearFragments();
  }

  // --------------------------------------------------------------------------
  // Per-frame animation
  // --------------------------------------------------------------------------

  private tick(ticker: Ticker): void {
    const dt = Math.min(ticker.deltaMS, 50) / 1000;
    this.elapsed += dt;

    this.animateBoxer(dt);
    this.animateBag(dt);
    this.animateFragments(dt);
    this.animateBoxerDust(dt);
    this.animateShake(dt);

    if (this.flash.alpha > 0) {
      this.flash.alpha = Math.max(0, this.flash.alpha - dt * 3.2);
    }
  }

  private animateBoxer(dt: number): void {
    const punching = this.phase === 'running';

    // The punch rate is the primary read on how far the round has gone.
    const target = punching ? PUNCH_SPEED_IDLE + (PUNCH_SPEED_MAX - PUNCH_SPEED_IDLE) * this.intensity : PUNCH_SPEED_IDLE;
    this.boxer.animationSpeed += (target - this.boxer.animationSpeed) * Math.min(1, dt * 6);

    // Watching for the impact frame is what couples the bag's swing to the
    // animation rather than to a timer that would drift out of step with it.
    const frame = this.boxer.currentFrame;
    if (frame !== this.lastImpactFrame && this.assets.impactFrames.includes(frame)) {
      this.onImpact();
      // The punch sound rides the same impact frame the bag's swing does, so the
      // hit is heard when it is seen. The audio player drops a repeat inside
      // ~90ms, which is what keeps a high multiplier -- where the animation is at
      // its fastest -- from turning into a buzz.
      playSound('round.punch');
    }
    this.lastImpactFrame = frame;
  }

  private onImpact(): void {
    if (this.destroyed || this.phase !== 'running') return;

    const force = 0.09 + this.intensity * 0.3;
    this.bagSwingVelocity += force;
    this.shake(1.5 + SHAKE_MAX_PX * this.intensity, 110);
  }

  private animateBag(dt: number): void {
    if (this.destroyed) return;

    // A damped pendulum: each punch adds velocity, gravity pulls it back, drag
    // settles it. Cheaper and steadier than interpolating between poses.
    const stiffness = 26;
    const damping = 2.6;
    const acceleration = -stiffness * this.bagSwing - damping * this.bagSwingVelocity;

    this.bagSwingVelocity += acceleration * dt;
    this.bagSwing += this.bagSwingVelocity * dt;

    this.bag.rotation = this.bagSwing * 0.38;
    // A little lateral give, so the bag does not look welded to its chain.
    this.bag.x = BAG_X + this.bagSwing * 12;
  }

  private animateShake(dt: number): void {
    if (this.elapsed >= this.shakeUntil) {
      this.shakeAmplitude *= Math.max(0, 1 - dt * 8);
    }
    if (this.shakeAmplitude < 0.05) {
      this.shakeAmplitude = 0;
      this.world.pivot.set(0, 0);
      return;
    }
    this.world.pivot.set(
      (Math.random() - 0.5) * 2 * this.shakeAmplitude,
      (Math.random() - 0.5) * 2 * this.shakeAmplitude,
    );
  }

  private shake(amplitude: number, durationMs: number): void {
    this.shakeAmplitude = Math.max(this.shakeAmplitude, amplitude);
    this.shakeUntil = Math.max(this.shakeUntil, this.elapsed + durationMs / 1000);
  }

  /**
   * The bag coming apart: chunks of canvas outwards, and a plume of sand of
   * which a good share is thrown over the boxer.
   */
  private burst(): void {
    this.clearFragments();

    const originX = this.bag.x;
    const originY = this.bag.y + 150;

    // --- torn canvas and leather ---------------------------------------------
    for (let i = 0; i < CHUNK_COUNT; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      // Biased towards the horizontal, which reads as the bag splitting rather
      // than a firework going off.
      const speed = 140 + Math.random() * 520;
      const scale = 0.4 + Math.random() * 1.3;

      this.spawn({
        container: this.chunks,
        texture: this.assets.particle,
        x: originX + (Math.random() - 0.5) * 70,
        y: originY + (Math.random() - 0.5) * 220,
        scale,
        tint: CHUNK_COLOURS[i % CHUNK_COLOURS.length]!,
        vx: Math.cos(angle) * speed * 1.4,
        vy: Math.sin(angle) * speed - 150,
        spin: (Math.random() - 0.5) * 16,
        maxLife: 0.9 + Math.random() * 1.1,
        sticky: false,
      });
    }

    // --- sand ----------------------------------------------------------------
    const aimed = Math.round(SAND_COUNT * SAND_AT_BOXER);

    for (let i = 0; i < SAND_COUNT; i += 1) {
      // A wide spread of grain sizes, because a plume of identically sized dots
      // reads as a pattern rather than as sand.
      const scale = 0.3 + Math.random() * Math.random() * 2.2;
      const x = originX + (Math.random() - 0.5) * 80;
      const y = originY + (Math.random() - 0.5) * 230;

      let vx: number;
      let vy: number;

      if (i < aimed) {
        // Aimed at a point somewhere over the boxer's body, with a slight lift so
        // the grain arcs down onto him instead of flying flat into him.
        const targetX = BOXER_HIT.left + Math.random() * (BOXER_HIT.right - BOXER_HIT.left);
        const targetY = BOXER_HIT.top + Math.random() * (BOXER_HIT.bottom - BOXER_HIT.top);
        const flight = 0.34 + Math.random() * 0.3;
        vx = (targetX - x) / flight;
        vy = (targetY - y) / flight - 0.5 * SAND_GRAVITY * flight;
      } else {
        const angle = Math.random() * Math.PI * 2;
        const speed = 90 + Math.random() * 430;
        vx = Math.cos(angle) * speed * 1.25;
        vy = Math.sin(angle) * speed - 120;
      }

      this.spawn({
        container: this.sand,
        texture: this.assets.sand,
        x,
        y,
        scale,
        tint: SAND_COLOURS[i % SAND_COLOURS.length]!,
        vx,
        vy,
        spin: (Math.random() - 0.5) * 6,
        // Long enough that settled grains are visible for a moment before they
        // fade, which is the whole point of settling them.
        maxLife: 1.3 + Math.random() * 1.1,
        sticky: true,
        guided: i < aimed,
      });
    }
  }

  private spawn(spec: {
    container: ParticleContainer;
    texture: Texture;
    x: number;
    y: number;
    scale: number;
    tint: number;
    vx: number;
    vy: number;
    spin: number;
    maxLife: number;
    sticky: boolean;
    guided?: boolean;
  }): void {
    const particle = new Particle({
      texture: spec.texture,
      x: spec.x,
      y: spec.y,
      scaleX: spec.scale,
      scaleY: spec.scale,
      rotation: Math.random() * Math.PI,
      anchorX: 0.5,
      anchorY: 0.5,
      tint: spec.tint,
    });

    spec.container.addParticle(particle);
    this.fragments.push({
      particle,
      container: spec.container,
      vx: spec.vx,
      vy: spec.vy,
      spin: spec.spin,
      life: 0,
      maxLife: spec.maxLife,
      sticky: spec.sticky,
      guided: spec.guided ?? false,
      landed: false,
    });
  }

  private animateFragments(dt: number): void {
    if (this.fragments.length === 0) return;

    let alive = 0;
    let landedThisFrame = 0;

    for (const f of this.fragments) {
      f.life += dt;
      if (f.life >= f.maxLife) {
        f.particle.alpha = 0;
        continue;
      }
      alive += 1;

      if (!f.landed) {
        f.vy += (f.sticky ? SAND_GRAVITY : CHUNK_GRAVITY) * dt;
        if (!f.guided) {
          // Frame-rate independent drag: the old form squared dt and so behaved
          // differently on a 120Hz screen than on a 60Hz one.
          f.vx *= Math.pow(f.sticky ? 0.82 : 0.88, dt * 60);
        }
        f.particle.x += f.vx * dt;
        f.particle.y += f.vy * dt;
        f.particle.rotation += f.spin * dt;

        if (f.sticky && this.settle(f)) landedThisFrame += 1;
      }

      // Fade over the back half of the fragment's life, and faster once it has
      // come to rest -- sand that has landed should read as settling dust, not
      // as a decal stuck to the figure.
      const t = f.life / f.maxLife;
      const fade = t < 0.5 ? 1 : 1 - (t - 0.5) * 2;
      f.particle.alpha = f.landed ? fade * 0.85 : fade;
    }

    if (landedThisFrame > 0) this.dustBoxer(landedThisFrame);

    this.chunks.update();
    this.sand.update();

    if (alive === 0) this.clearFragments();
  }

  /**
   * Stops a grain that has reached the boxer or the floor.
   *
   * Returns true only for the boxer, since that is the collision the figure
   * reacts to. The floor is where the rest of the plume ends up, and a grain
   * lying on the boards is worth drawing but not worth reacting to.
   */
  private settle(f: Fragment): boolean {
    const { x, y } = f.particle;

    if (x > BOXER_HIT.left && x < BOXER_HIT.right && y > BOXER_HIT.top && y < BOXER_HIT.bottom) {
      f.landed = true;
      f.vx = 0;
      f.vy = 0;
      f.spin = 0;
      // Cut what is left of its life short, so the dusting fades rather than
      // sitting on the figure until the next round.
      f.maxLife = Math.min(f.maxLife, f.life + 0.55 + Math.random() * 0.5);
      return true;
    }

    if (y >= GROUND_Y) {
      f.landed = true;
      f.particle.y = GROUND_Y - Math.random() * 3;
      f.vx = 0;
      f.vy = 0;
      f.spin = 0;
    }
    return false;
  }

  /**
   * The figure's reaction to being hit by the sand: a flinch away from the bag
   * and a sandy tint that fades.
   *
   * Driven by how many grains landed, so a direct hit reads harder than the
   * tail of the plume drifting over him.
   */
  private dustBoxer(grains: number): void {
    this.boxerDust = Math.min(1, this.boxerDust + grains * 0.05);
    this.boxerFlinch = Math.min(1, this.boxerFlinch + grains * 0.06);
    // A second, smaller jolt as the sand arrives, a beat after the burst itself.
    // Capped on the grain count: most of the aimed plume lands within a frame or
    // two of itself, and scaling straight off it produced a jolt several times
    // the crash's own.
    this.shake(4 + Math.min(grains, 6) * 1.6, 90);
  }

  private animateBoxerDust(dt: number): void {
    if (this.boxerDust <= 0 && this.boxerFlinch <= 0) return;

    this.boxerDust = Math.max(0, this.boxerDust - dt * 0.55);
    this.boxerFlinch = Math.max(0, this.boxerFlinch - dt * 2.2);

    this.boxer.tint = mixColour(0xffffff, SAND_TINT, this.boxerDust * 0.8);
    // Knocked back and rocked away from the bag, which stands to the right.
    this.boxer.x = BOXER_X - this.boxerFlinch * 9;
    this.boxer.rotation = -this.boxerFlinch * 0.05;

    if (this.boxerDust === 0 && this.boxerFlinch === 0) {
      this.boxer.tint = 0xffffff;
      this.boxer.x = BOXER_X;
      this.boxer.rotation = 0;
    }
  }

  private clearFragments(): void {
    if (this.fragments.length === 0) return;
    this.chunks.removeParticles();
    this.sand.removeParticles();
    this.chunks.update();
    this.sand.update();
    this.fragments = [];
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Blends two packed RGB colours, for tinting the boxer as sand covers him. */
function mixColour(from: number, to: number, t: number): number {
  const amount = clamp(t, 0, 1);
  const r = Math.round((((from >> 16) & 0xff) * (1 - amount)) + (((to >> 16) & 0xff) * amount));
  const g = Math.round((((from >> 8) & 0xff) * (1 - amount)) + (((to >> 8) & 0xff) * amount));
  const b = Math.round(((from & 0xff) * (1 - amount)) + ((to & 0xff) * amount));
  return (r << 16) | (g << 8) | b;
}

function multiplierStyle(fill: number): TextStyle {
  return new TextStyle({
    fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
    fontSize: 96,
    fontWeight: '800',
    fill,
    letterSpacing: -2,
    dropShadow: { color: 0x000000, alpha: 0.55, blur: 6, distance: 3, angle: Math.PI / 3 },
  });
}

/** The multiplier warms from grey through gold to red as the round runs on. */
function multiplierColour(x100: number): number {
  if (x100 >= 1000) return 0xff4d4f;
  if (x100 >= 500) return 0xff7a45;
  if (x100 >= 200) return 0xf0b429;
  return 0xe8eef7;
}
